import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Utils } from '@adobe/aem-react-editable-components';
import { useLazyQuery, useMutation } from '@apollo/client';
import { REMOVE_NEW_TAG_FROM_EVENT_MUTATION, SET_PARTICIPANTS_MUTATION } from 'Queries/Mutations';
import { getAllEventsQuery } from 'Queries/Queries';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import { EventMetaData } from 'components/Event/EventMetaData/EventMetaData';
import { OuterBox } from 'components/OuterBox/OuterBox';
import { isNewEntry } from 'components/OverviewLists/utils/isNewEntry';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { PageTitleSection } from 'components/PageTitleSection/PageTitleSection';
import { Separator } from 'components/Separator/Separator';

import { TENANT_ID } from 'global/constants';

import { getIsRegistrationExpired } from 'utils/EventDateUtils';
import { checkKeyDown } from 'utils/FormUtils';
import { getEventsOverviewPath } from 'utils/UrlUtil';
import { getTenantId } from 'utils/tenantUtil';

import { EventRegisterCompanyFields } from './EventRegisterCompanyFields';
import { EventRegisterFormFooter } from './EventRegisterFormFooter';
import { EventRegisterParticipantsSection } from './EventRegisterParticipantsSection';
import { EventRegisterSuccessModal } from './EventRegisterSuccessModal';
import { YEventRegistration } from './YEventRegistration';
import { isRegisteredParticipantState } from './eventParticipantComparison.utils';
import {
  hasPendingParticipantEmails,
  syncEditingParticipantEmailIds,
} from './eventParticipantEmail.utils';
import { getRegisteredParticipantId } from './eventParticipantId.utils';

import './EventRegisterForm.scss';

export const EventRegisterForm = ({
  event,
  userStatusData,
  installer,
  refetchEvent,
  setQrScannerOpen,
}) => {
  const isInEditor = Utils.isInEditor();

  const { id: eventId, registration } = event;
  const { userStatus } = userStatusData;
  const { iamType } = userStatus;

  // state & hooks
  const tenant = getTenantId();
  const history = useHistory();
  const [remainingAllowed, setRemainingAllowed] = useState(null);
  const [guestNotAllowedError, setGuestNotAllowedError] = useState(false);
  const [errorFromRequest, setErrorFromRequest] = useState({});

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isCancelAllModalOpen, setIsCancelAllModalOpen] = useState(false);
  const [isUnregister, setIsUnregister] = useState(false);

  const mapParticipantsForMutation = useCallback(
    (participants) =>
      participants.map(({ fachkraftId, firstName, lastName, eMail }) => ({
        ...(fachkraftId ? { fachkraftId: Number(fachkraftId) } : {}),
        firstName,
        lastName,
        eMail,
      })),
    []
  );

  // mutations & queries
  const [
    setParticipantsMutation,
    { data: setParticipantsResponse, loading: isSavingParticipants },
  ] = useMutation(SET_PARTICIPANTS_MUTATION, {
    onError({ graphQLErrors, networkError }) {
      if (graphQLErrors) setErrorFromRequest(graphQLErrors[0].extensions);
      if (networkError) console.error(`[Network error]: ${networkError}`);
    },
  });

  const submitRegistration = async (values) => {
    const mappedParticipants = mapParticipantsForMutation(values.participants);
    const hasServerRegistration = registration.participants.length > 0;

    if (values.participants.length === 0 && hasServerRegistration) {
      setIsUnregister(true);
      await setParticipantsMutation({
        variables: {
          eventId,
          unregister: true,
          participants: [],
        },
      });
    } else {
      setIsUnregister(false);
      await setParticipantsMutation({
        variables: {
          eventId,
          participants: mappedParticipants,
        },
      });
    }

    await refetchEvent();
  };

  const [removeNewTag] = useMutation(REMOVE_NEW_TAG_FROM_EVENT_MUTATION, {
    refetchQueries: () => [{ query: getAllEventsQuery }],
    awaitRefetchQueries: false,
    variables: {
      eventId,
    },
  });

  const isEiamUser = iamType === 'EIAM';

  const isRegisteredOnServer = registration.participants.length > 0;

  const eiamDefaultParticipant = useMemo(() => {
    if (!isEiamUser) return null;
    const fullName = userStatus.fullName || '';
    const spaceIndex = fullName.indexOf(' ');
    const firstName = spaceIndex >= 0 ? fullName.slice(0, spaceIndex) : fullName;
    const lastName = spaceIndex >= 0 ? fullName.slice(spaceIndex + 1) : fullName;
    return {
      id: 'eiam-default-participant',
      firstName,
      lastName,
      eMail: userStatus.installerEmail || '',
      fachkraftId: null,
    };
  }, [isEiamUser, userStatus.fullName, userStatus.installerEmail]);

  const [reloadEvents] = useLazyQuery(getAllEventsQuery);

  const getParticipants = () => {
    if (registration.participants.length) {
      return registration.participants.map(({ id, firstName, lastName, eMail, fachkraftId }) => ({
        id: getRegisteredParticipantId(id),
        firstName,
        lastName,
        eMail,
        fachkraftId: fachkraftId ?? null,
      }));
    }
    if (isEiamUser && eiamDefaultParticipant) {
      return [eiamDefaultParticipant];
    }
    return [];
  };

  const [editingParticipantEmailIds, setEditingParticipantEmailIds] = useState(() =>
    syncEditingParticipantEmailIds(getParticipants())
  );

  const formik = useFormik({
    initialValues: {
      firmenname1: '',
      firmenname2: '',
      firmenname3: '',
      installerEmail: userStatus.installerEmail,
      participants: getParticipants(),
    },
    validationSchema: YEventRegistration,
    onSubmit: submitRegistration,
  });

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
    handleSubmit,
    isValid,
    isSubmitting,
    validateForm,
  } = formik;

  const isRegistered = isRegisteredParticipantState(values.participants, registration.participants);

  useEffect(() => {
    setEditingParticipantEmailIds((previousIds) =>
      syncEditingParticipantEmailIds(values.participants, previousIds)
    );
    validateForm();
  }, [values.participants, validateForm]);

  const syncedEditingParticipantEmailIds = useMemo(
    () => syncEditingParticipantEmailIds(values.participants, editingParticipantEmailIds),
    [values.participants, editingParticipantEmailIds]
  );

  const setParticipantEmailEditing = useCallback((participantId, isEditing) => {
    setEditingParticipantEmailIds((previousIds) => {
      if (isEditing && previousIds.has(participantId)) {
        return previousIds;
      }

      if (!isEditing && !previousIds.has(participantId)) {
        return previousIds;
      }

      const nextIds = new Set(previousIds);
      if (isEditing) {
        nextIds.add(participantId);
      } else {
        nextIds.delete(participantId);
      }
      return nextIds;
    });
  }, []);

  const hasPendingEmails = useMemo(
    () => hasPendingParticipantEmails(values.participants, syncedEditingParticipantEmailIds),
    [values.participants, syncedEditingParticipantEmailIds]
  );

  const isRegistrationExpired = getIsRegistrationExpired(event);

  const freePlaces = event.maxParticipants - registration.eventParticipantsNumber;
  let maxParticipants;
  if (isRegistrationExpired) {
    maxParticipants = registration.participants.length;
  } else if (freePlaces > 0) {
    maxParticipants = Math.min(
      event.maxParticipantsPerCompany,
      freePlaces + registration.participants.length
    );
  } else if (registration.participants.length > 0) {
    maxParticipants = registration.participants.length;
  } else {
    maxParticipants = 0;
  }

  const canRegister =
    !isRegistrationExpired && (isEiamUser || maxParticipants > 0 || event.disableMaxParticipation);

  const shouldFormRender = canRegister || isRegisteredOnServer || values.participants.length > 0;

  // effects
  useEffect(() => {
    if (userStatus?.installerEmail) {
      setFieldValue('installerEmail', userStatus.installerEmail);
    }
  }, [userStatus?.installerEmail, setFieldValue]);

  useEffect(() => {
    const handleRemoveTag = async () => {
      await removeNewTag();
    };

    if (!isInEditor && isNewEntry(event.tags)) {
      // fail silent
      handleRemoveTag();
    }
  }, [event.tags, isInEditor, removeNewTag]);

  useEffect(() => {
    if (
      !setParticipantsResponse &&
      errorFromRequest?.code === 'BAD_USER_INPUT' &&
      errorFromRequest?.data?.action === 'booking' &&
      errorFromRequest?.data?.availability !== 'undefined'
    ) {
      setRemainingAllowed(errorFromRequest.data.availability);
    } else if (
      !setParticipantsResponse &&
      errorFromRequest?.code === 'BAD_USER_INPUT' &&
      errorFromRequest?.data?.action === 'guestNotAllowed'
    ) {
      setGuestNotAllowedError(true);
    } else if (setParticipantsResponse?.setParticipants) {
      setGuestNotAllowedError(false);
      setModalIsOpen(true);
    }
  }, [setParticipantsResponse, errorFromRequest]);

  useEffect(() => {
    if (iamType === 'CIAM') {
      if (installer.installer.firmenname1) {
        setFieldValue('firmenname1', installer.installer.firmenname1);
      }
      if (installer.installer.firmenname2) {
        setFieldValue('firmenname2', installer.installer.firmenname2);
      }
      if (installer.installer.firmenname3 && tenant === TENANT_ID.BAG) {
        setFieldValue('firmenname3', installer.installer.firmenname3);
      }
    } else if (iamType === 'EIAM') {
      setFieldValue('firmenname1', 'Interner Mitarbeiter');
    }
  }, [installer, iamType, tenant, setFieldValue]);

  // functions
  const closeModal = () => {
    reloadEvents();
    setModalIsOpen(false);
    history.push(getEventsOverviewPath());
  };

  const handleRemoveParticipant = useCallback(
    (index) => {
      const newParticipants = values.participants.filter(
        (_, participantIndex) => participantIndex !== index
      );
      setFieldValue('participants', newParticipants);
    },
    [values.participants, setFieldValue]
  );

  const handleCancelAllRegistration = async () => {
    setIsUnregister(true);
    setIsCancelAllModalOpen(false);
    setFieldValue('participants', []);
    await setParticipantsMutation({
      variables: {
        eventId,
        unregister: true,
        participants: [],
      },
    });
    await refetchEvent();
  };

  const footerActionTitle = isRegisteredOnServer ? 'Speichern' : 'Anmelden';
  const footerActionDisabled = isRegisteredOnServer
    ? isRegistered || !isValid || isSubmitting || hasPendingEmails || isSavingParticipants
    : !isValid || isSubmitting || hasPendingEmails || isSavingParticipants;

  return (
    <>
      <EventRegisterSuccessModal
        isOpen={modalIsOpen}
        onClose={closeModal}
        isUnregister={isUnregister}
      />

      <CustomModal
        title="Wirklich absagen?"
        content={
          <div className="event-register-form__modal-content-wrapper">
            Willst du den Termin wirklich für dich und alle deine Teilnehmer absagen?
          </div>
        }
        ctaButtons={
          <>
            <ActionButton
              onClick={handleCancelAllRegistration}
              title="Absagen"
              inverted
              disabled={isSavingParticipants}
            />
            <ActionButton
              onClick={() => setIsCancelAllModalOpen(false)}
              title="Abbrechen"
              inverted
              disabled={isSavingParticipants}
            />
          </>
        }
        closeCancelEventModal={() => setIsCancelAllModalOpen(false)}
        isOpen={isCancelAllModalOpen}
      />

      <PageTitleSection
        title="Anmeldung"
        subText="Hier können Sie sich für die von Ihnen ausgewählte Veranstaltung anmelden. Sie können den Termin anschließend nach erfolgreicher Anmeldung auf Ihrer e-fix Startseite sehen und jeder Zeit wieder absagen oder die Teilnehmer ändern."
        notFramed
      />
      <OuterBox>
        <PageTitle text="Ausgewählte Veranstaltung" notFramed />

        <Separator color={getTenantId() === TENANT_ID.AVA ? 'homepage' : 'secondary'} />
        <EventMetaData event={event} iamType={iamType} />
        {/* if new users can be registered or there are already registered users */}
        <div className="event-register-form">
          {(userStatus.role === 'EVENT_USER' || userStatus.role === 'POWER_USER') &&
            !event.meetingLink && (
              <div className="event-card__participants-list-button-wrapper">
                <ActionButton
                  title="QR Code scannen"
                  onClick={() => setQrScannerOpen(true)}
                  inverted
                />
              </div>
            )}
        </div>
        {shouldFormRender ? (
          <form className="event-register-form" onSubmit={handleSubmit} onKeyDown={checkKeyDown}>
            <div className="event-register-form__registration-wrapper">
              <PageTitle text="Anmeldung:" notFramed />
            </div>
            <EventRegisterCompanyFields
              values={values}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              tenant={tenant}
              iamType={iamType}
            />
            {!isEiamUser && (
              <EventRegisterParticipantsSection
                disableMaxParticipation={event.disableMaxParticipation}
                canGuestRegister={event.allowGuestRegistration}
                maxParticipants={maxParticipants}
                participants={values.participants}
                handleBlur={handleBlur}
                formik={formik}
                isRegistrationExpired={isRegistrationExpired}
                iamType={iamType}
                editingParticipantEmailIds={syncedEditingParticipantEmailIds}
                setParticipantEmailEditing={setParticipantEmailEditing}
                onRemoveParticipant={handleRemoveParticipant}
              />
            )}
            {shouldFormRender && (
              <EventRegisterFormFooter
                actionTitle={footerActionTitle}
                actionDisabled={footerActionDisabled}
                actionType="submit"
                cancelAllTitle={isRegisteredOnServer ? 'Alle Teilnehmenden abmelden' : undefined}
                onCancelAll={isRegisteredOnServer ? () => setIsCancelAllModalOpen(true) : undefined}
                cancelAllDisabled={isSavingParticipants}
                remainingAllowed={remainingAllowed}
                guestNotAllowedError={guestNotAllowedError}
                values={values}
                errors={errors}
                touched={touched}
              />
            )}
          </form>
        ) : (
          <div className="event-register-form__deadline-passed">
            Registrierung für dieses Event ist geschlossen.
          </div>
        )}
      </OuterBox>
    </>
  );
};

EventRegisterForm.propTypes = {
  event: PropTypes.object.isRequired,
  userStatusData: PropTypes.object.isRequired,
  setQrScannerOpen: PropTypes.func.isRequired,
  installer: PropTypes.object.isRequired,
  refetchEvent: PropTypes.func.isRequired,
};
